import { useState } from 'react'
import type { AcaoOtimizacaoMeta, RecomendacaoResposta } from '@baiocchi/shared'
import {
  Alerta,
  AreaTexto,
  Botao,
  CabecalhoCartao,
  Cartao,
  Confirmacao,
  Esqueleto,
  EstadoVazio,
  Etiqueta,
  useAvisos,
} from '@baiocchi/ui'
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query'

import { chaves } from '../../api/chaves'
import { metaAdsApi } from '../../api/meta-ads'
import { mensagemDoErro } from '../../api/erros'
import { EstadoDeErro } from '../../componentes/estados-tela'
import { IconeFaisca, IconePausar } from '../../componentes/icones'
import { useFiltrosUrl } from '../../ganchos/usar-filtros-url'
import { CabecalhoPagina } from '../../layout/layout-app'
import { formatarMetrica } from './componentes'
import { FiltrosMetaAds } from './filtros'

/**
 * TELA-META-07 — Recomendações da IA (§10, §11, §16).
 *
 * ## Nada acontece sem confirmação explícita
 *
 * O §31 é categórico: *IA observa, IA analisa, IA recomenda, HUMANO aprova, SISTEMA
 * executa.* Nesta tela isso é o modal de confirmação — a terceira das quatro barreiras. Ele
 * mostra **o que vai mudar** (antes → depois) e **o que isso significa**, e exige um motivo
 * escrito, que vai para o histórico.
 *
 * O motivo é obrigatório (mínimo de 10 caracteres, validado no servidor) e não é
 * burocracia: sem ele, o histórico do §17 responderia "o que mudou" mas não "por quê", que
 * é a metade que permite julgar depois se a IA melhorou a performance.
 *
 * ## `executavel` vem do servidor
 *
 * O botão de aplicar aparece só quando o servidor diz que a ação é possível — o que depende
 * de permissão, da ação sugerida existir, e de `META_ADS_ESCRITA_ATIVA`. A tela não conhece
 * a terceira condição, e um botão que aparece e falha é pior do que um botão ausente com a
 * explicação ao lado.
 */
export function PaginaMetaAdsRecomendacoes() {
  const { filtros, definir } = useFiltrosUrl()
  const cliente = useQueryClient()
  const avisos = useAvisos()

  const parametros = {
    contaId: filtros.contaId,
    status: filtros.status ?? 'ABERTA',
    nivel: filtros.nivel,
  }

  const [contas, consulta] = useQueries({
    queries: [
      {
        queryKey: chaves.metaAds.contas,
        queryFn: ({ signal }: { signal: AbortSignal }) => metaAdsApi.contas(signal),
        staleTime: 300_000,
      },
      {
        queryKey: chaves.metaAds.recomendacoes(parametros),
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          metaAdsApi.recomendacoes(parametros, signal),
        staleTime: 60_000,
      },
    ],
  })

  const [emConfirmacao, setEmConfirmacao] = useState<RecomendacaoResposta | null>(null)
  const [motivo, setMotivo] = useState('')

  const executar = useMutation({
    mutationFn: (entrada: {
      readonly recomendacao: RecomendacaoResposta
      readonly motivo: string
    }) => {
      const { recomendacao } = entrada
      const acao = recomendacao.acaoSugerida
      if (acao === null) throw new Error('Recomendação sem ação executável.')

      return metaAdsApi.executarAcao({
        contaId: recomendacao.contaId,
        nivel: recomendacao.nivel,
        idExterno: recomendacao.idExterno,
        acao,
        recomendacaoId: Number.parseInt(recomendacao.id, 10),
        motivo: entrada.motivo,
        // Enviado explicitamente pela tela — é a barreira de intenção do §11, e por isso
        // não é um valor que o cliente de API preenche por conta própria.
        confirmado: true,
        ...(acao === 'ALTERAR_ORCAMENTO'
          ? { orcamentoDiario: lerOrcamentoSugerido(recomendacao) }
          : {}),
      })
    },
    onSuccess: (acao) => {
      // A ação pode voltar `FALHOU`: o serviço registra a tentativa e devolve o registro em
      // vez de lançar, para o gestor ver a causa. Aqui isso vira aviso de erro, não sucesso.
      if (acao.status === 'FALHOU') {
        avisos.mostrar({
          tom: 'erro',
          titulo: 'A Meta recusou a alteração',
          descricao: acao.erroMensagem ?? 'Sem detalhe. A tentativa ficou registrada no histórico.',
        })
      } else {
        avisos.mostrar({
          tom: 'sucesso',
          titulo: 'Otimização aplicada',
          descricao:
            'A Meta aceitou a alteração. O efeito na entrega aparece em alguns minutos e o ' +
            'registro está no histórico.',
        })
      }
      setEmConfirmacao(null)
      setMotivo('')
      void cliente.invalidateQueries({ queryKey: chaves.metaAds.todos })
    },
    /**
     * Falha de **transporte** (403, 409, 422, 500, queda de rede) — distinta da falha
     * de domínio tratada acima, em que a Meta recusa e o serviço devolve o registro
     * com `status: 'FALHOU'`.
     *
     * Sem isto, o erro era engolido: o modal continuava aberto, nada aparecia, e numa
     * ação que **pausa anúncio e move orçamento** o gestor ficava sem saber se a
     * alteração foi aplicada. O modal segue aberto de propósito — assim a ação pode
     * ser repetida depois de entender a causa.
     */
    onError: (erro) =>
      avisos.erro('Não foi possível aplicar a otimização', mensagemDoErro(erro)),
  })

  const descartar = useMutation({
    mutationFn: (id: string) => metaAdsApi.descartarRecomendacao(id),
    onSuccess: () => {
      avisos.mostrar({ tom: 'info', titulo: 'Recomendação descartada' })
      void cliente.invalidateQueries({ queryKey: chaves.metaAds.todos })
    },
    // Sem aviso, a recomendação reaparecia na próxima carga e parecia que o botão
    // não fazia nada — quando o que houve foi um erro que ninguém mostrou.
    onError: (erro) => avisos.erro('Não foi possível descartar', mensagemDoErro(erro)),
  })

  const narrar = useMutation({
    mutationFn: (id: string) => metaAdsApi.narrarRecomendacao(id),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: chaves.metaAds.todos }),
    /**
     * Cada narração é uma chamada **paga** ao provedor de IA. Falhando em silêncio, o
     * gestor clica de novo — e paga de novo — sem nunca ver a causa.
     */
    onError: (erro) => avisos.erro('Não foi possível gerar a análise', mensagemDoErro(erro)),
  })

  const recomendacoes = consulta.data?.dados ?? []

  return (
    <>
      <CabecalhoPagina
        titulo="Recomendações"
        descricao="O que o sistema sugere fazer, com a evidência que sustenta cada sugestão."
      />

      <FiltrosMetaAds
        filtros={filtros}
        definir={definir}
        contas={contas.data?.dados ?? []}
        carregandoContas={contas.isLoading}
      />

      {consulta.isError ? (
        <EstadoDeErro erro={consulta.error} aoTentarNovamente={() => void consulta.refetch()} />
      ) : null}

      {consulta.isLoading ? <Esqueleto className="h-48 w-full" /> : null}

      {consulta.isSuccess && recomendacoes.length === 0 ? (
        <Cartao>
          <EstadoVazio
            titulo="Nenhuma recomendação aberta"
            descricao="A análise roda automaticamente após cada sincronização de métricas. Sem recomendação significa que nenhum alvo desviou do padrão da conta no período."
          />
        </Cartao>
      ) : null}

      <div className="space-y-4">
        {recomendacoes.map((recomendacao) => (
          <CartaoRecomendacao
            key={recomendacao.id}
            recomendacao={recomendacao}
            aoAplicar={() => {
              setEmConfirmacao(recomendacao)
              setMotivo(recomendacao.titulo)
            }}
            aoDescartar={() => descartar.mutate(recomendacao.id)}
            aoNarrar={() => narrar.mutate(recomendacao.id)}
            narrando={narrar.isPending && narrar.variables === recomendacao.id}
          />
        ))}
      </div>

      {emConfirmacao !== null ? (
        <Confirmacao
          aberto
          titulo="Confirmar alteração na Meta"
          onFechar={() => {
            setEmConfirmacao(null)
            setMotivo('')
          }}
          onConfirmar={() => {
            // Validação no clique, e não botão desabilitado: um botão que não responde deixa
            // o gestor sem saber o que falta. A mensagem diz.
            if (motivo.trim().length < 10) {
              avisos.mostrar({
                tom: 'atencao',
                titulo: 'Escreva o motivo',
                descricao: 'O motivo fica no histórico e é o que permite julgar a decisão depois.',
              })
              return
            }
            executar.mutate({ recomendacao: emConfirmacao, motivo })
          }}
          rotuloConfirmar="Aplicar na Meta"
          varianteConfirmar="perigo"
          processando={executar.isPending}
        >
          <div className="space-y-3 text-sm">
            <Alerta tom="atencao">
              Esta ação altera a conta de anúncio de verdade. {consequenciaDe(emConfirmacao)}
            </Alerta>

            <dl className="space-y-1">
              <div className="flex justify-between gap-3">
                <dt className="text-conteudo-suave">Alvo</dt>
                <dd className="text-right font-medium">{emConfirmacao.nomeAlvo}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-conteudo-suave">Ação</dt>
                <dd className="text-right font-medium">
                  {ROTULO_ACAO[emConfirmacao.acaoSugerida ?? 'PAUSAR_ANUNCIO']}
                </dd>
              </div>
              {emConfirmacao.valorSugerido !== null ? (
                <div className="flex justify-between gap-3">
                  <dt className="text-conteudo-suave">Mudança</dt>
                  <dd className="text-right font-medium tabular-nums">
                    {descreverMudanca(emConfirmacao)}
                  </dd>
                </div>
              ) : null}
            </dl>

            <label className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-conteudo-suave">
                Motivo (fica registrado no histórico)
              </span>
              <AreaTexto
                value={motivo}
                onChange={(evento) => setMotivo(evento.target.value)}
                rows={3}
                maxLength={1000}
                placeholder="Por que esta alteração está sendo feita?"
              />
              {motivo.trim().length < 10 ? (
                <span className="mt-1 block text-xs text-conteudo-fraco">
                  Escreva ao menos 10 caracteres — é o que permite julgar a decisão depois.
                </span>
              ) : null}
            </label>
          </div>
        </Confirmacao>
      ) : null}
    </>
  )
}

function CartaoRecomendacao({
  recomendacao,
  aoAplicar,
  aoDescartar,
  aoNarrar,
  narrando,
}: {
  readonly recomendacao: RecomendacaoResposta
  readonly aoAplicar: () => void
  readonly aoDescartar: () => void
  readonly aoNarrar: () => void
  readonly narrando: boolean
}) {
  return (
    <Cartao semPadding>
      <CabecalhoCartao
        titulo={
          <span className="flex flex-wrap items-center gap-2">
            <Etiqueta tom={TOM_SEVERIDADE[recomendacao.severidade]}>
              {ROTULO_SEVERIDADE[recomendacao.severidade]}
            </Etiqueta>
            {recomendacao.titulo}
          </span>
        }
        descricao={
          <span className="text-xs">
            {recomendacao.nomeAlvo} · {recomendacao.contaNome}
            {recomendacao.score !== null ? ` · score ${recomendacao.score}/100` : ''}
          </span>
        }
      />

      <div className="space-y-3 p-4 sm:p-5">
        <p className="text-sm leading-relaxed text-conteudo">{recomendacao.resumo}</p>

        {recomendacao.narrativaIa !== null ? (
          <div className="rounded border border-borda bg-superficie-2 p-3">
            <p className="mb-1 flex items-center gap-1.5 text-xs font-medium text-conteudo-suave">
              <IconeFaisca className="h-4 w-4" />
              Análise
            </p>
            <p className="whitespace-pre-line text-sm text-conteudo">{recomendacao.narrativaIa}</p>
          </div>
        ) : null}

        <EvidenciaDetalhada evidencia={recomendacao.evidencia} />

        <div className="flex flex-wrap items-center gap-2 border-t border-borda pt-3">
          {recomendacao.executavel ? (
            <Botao
              variante="perigo"
              tamanho="sm"
              onClick={aoAplicar}
              iconeEsquerda={<IconePausar className="h-4 w-4" />}
            >
              {ROTULO_ACAO[recomendacao.acaoSugerida ?? 'PAUSAR_ANUNCIO']}
            </Botao>
          ) : (
            <span className="text-xs text-conteudo-fraco">
              {recomendacao.motivoNaoExecutavel}
            </span>
          )}

          {recomendacao.narrativaIa === null ? (
            <Botao variante="fantasma" tamanho="sm" onClick={aoNarrar} carregando={narrando}>
              Explicar
            </Botao>
          ) : null}

          <Botao variante="fantasma" tamanho="sm" onClick={aoDescartar}>
            Descartar
          </Botao>
        </div>
      </div>
    </Cartao>
  )
}

/**
 * A evidência numérica que sustenta a recomendação.
 *
 * Exibida sempre, não escondida atrás de um "ver detalhes": o §7 e o §11 exigem que a
 * decisão seja justificada com números, e uma recomendação de pausar anúncio sem os números
 * ao lado é um botão que o gestor aprende a clicar sem ler.
 */
function EvidenciaDetalhada({
  evidencia,
}: {
  readonly evidencia: Readonly<Record<string, unknown>>
}) {
  const entradas = Object.entries(evidencia).filter(
    ([, valor]) => typeof valor === 'number' || typeof valor === 'string',
  )

  if (entradas.length === 0) return null

  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-3">
      {entradas.map(([chave, valor]) => (
        <div key={chave} className="flex justify-between gap-2">
          <dt className="text-conteudo-fraco">{rotularChave(chave)}</dt>
          <dd className="tabular-nums text-conteudo-suave">
            {formatarValorEvidencia(chave, valor as number | string)}
          </dd>
        </div>
      ))}
    </dl>
  )
}

const ROTULO_CHAVE: Readonly<Record<string, string>> = {
  gasto: 'Investimento',
  leads: 'Leads',
  cpl: 'CPL',
  cplMedianoConta: 'CPL mediano da conta',
  ctr: 'CTR',
  cpc: 'CPC',
  cpm: 'CPM',
  cliques: 'Cliques',
  impressoes: 'Impressões',
  alcance: 'Alcance',
  frequencia: 'Frequência',
  razao: 'Razão sobre a mediana',
  score: 'Score',
  variacaoCplPercentual: 'Variação do CPL',
  variacaoCtrPercentual: 'Variação do CTR',
  variacaoCpcPercentual: 'Variação do CPC',
  fatiaDoGasto: 'Fatia do investimento',
  grauFadiga: 'Grau de fadiga',
  sinais: 'Sinais',
  janelaDias: 'Janela (dias)',
  mediaRecente: 'Média recente',
  mediaAnterior: 'Média anterior',
  variacaoPercentual: 'Variação',
  alcanceAproximado: 'Alcance aproximado',
}

function rotularChave(chave: string): string {
  return ROTULO_CHAVE[chave] ?? chave
}

function formatarValorEvidencia(chave: string, valor: number | string): string {
  if (typeof valor === 'string') return valor
  if (chave.startsWith('variacao')) return `${valor > 0 ? '+' : ''}${valor}%`
  if (chave === 'fatiaDoGasto') return `${Math.round(valor * 100)}%`
  if (chave === 'gasto' || chave.startsWith('cpl') || chave === 'cpc' || chave === 'cpm') {
    return formatarMetrica(valor, 'MOEDA')
  }
  if (chave === 'ctr') return formatarMetrica(valor, 'PERCENTUAL')
  if (chave === 'frequencia' || chave === 'razao') return formatarMetrica(valor, 'DECIMAL')
  return formatarMetrica(valor, 'INTEIRO')
}

const TOM_SEVERIDADE = {
  CRITICO: 'perigo',
  ATENCAO: 'atencao',
  OPORTUNIDADE: 'sucesso',
  INFORMATIVO: 'info',
} as const

const ROTULO_SEVERIDADE = {
  CRITICO: 'Crítico',
  ATENCAO: 'Atenção',
  OPORTUNIDADE: 'Oportunidade',
  INFORMATIVO: 'Informativo',
} as const

const ROTULO_ACAO: Readonly<Record<AcaoOtimizacaoMeta, string>> = {
  PAUSAR_ANUNCIO: 'Pausar anúncio',
  ATIVAR_ANUNCIO: 'Ativar anúncio',
  PAUSAR_CONJUNTO: 'Pausar conjunto',
  ATIVAR_CONJUNTO: 'Ativar conjunto',
  PAUSAR_CAMPANHA: 'Pausar campanha',
  ATIVAR_CAMPANHA: 'Ativar campanha',
  ALTERAR_ORCAMENTO: 'Alterar orçamento',
  CRIAR_VARIACAO: 'Criar variação',
  DUPLICAR_ANUNCIO: 'Duplicar anúncio',
}

/**
 * A consequência, em uma frase.
 *
 * O §11 pede explicitamente para "explicar consequência". Não é o mesmo que descrever a
 * ação: "pausar anúncio" diz o que acontece no botão; "a entrega para em alguns minutos e o
 * histórico é preservado" diz o que acontece com o negócio.
 */
function consequenciaDe(recomendacao: RecomendacaoResposta): string {
  switch (recomendacao.acaoSugerida) {
    case 'PAUSAR_ANUNCIO':
    case 'PAUSAR_CONJUNTO':
    case 'PAUSAR_CAMPANHA':
      return (
        'A entrega para em alguns minutos e o investimento deixa de ser consumido. O histórico ' +
        'e o aprendizado da Meta são preservados — é possível reativar depois.'
      )
    case 'ATIVAR_ANUNCIO':
    case 'ATIVAR_CONJUNTO':
    case 'ATIVAR_CAMPANHA':
      return 'A entrega volta a consumir orçamento diário. A Meta pode levar algumas horas para reaprender.'
    case 'ALTERAR_ORCAMENTO':
      return (
        'O orçamento diário muda a partir de agora. Ajustes de orçamento devolvem o conjunto à ' +
        'fase de aprendizado, e o custo por lead pode oscilar por alguns dias antes de estabilizar.'
      )
    default:
      return 'A alteração é enviada à Meta e registrada no histórico de otimizações.'
  }
}

function descreverMudanca(recomendacao: RecomendacaoResposta): string {
  const valor = recomendacao.valorSugerido
  if (valor === null) return '—'

  const novo = valor.orcamentoDiario
  const anterior = valor.orcamentoDiarioAnterior

  if (typeof novo === 'number' && typeof anterior === 'number') {
    return `${formatarMetrica(anterior, 'MOEDA')} → ${formatarMetrica(novo, 'MOEDA')} por dia`
  }
  if (typeof novo === 'number') return `${formatarMetrica(novo, 'MOEDA')} por dia`
  return '—'
}

function lerOrcamentoSugerido(recomendacao: RecomendacaoResposta): number | undefined {
  const valor = recomendacao.valorSugerido?.orcamentoDiario
  return typeof valor === 'number' ? valor : undefined
}
